import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Save, History, Code2 } from 'lucide-react';
import OilAdminJackpotRate from './OilAdminJackpotRate';
import OilAdminMissions from './OilAdminMissions';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

// grupos do formulário: cada campo aponta para um caminho dentro do config
const buildGroups = (cfg) => {
  const g = [];
  const fac = cfg?.economy?.facilities || {};
  g.push({
    title: 'Usina de Petróleo (por nível)',
    fields: (fac.refinery || []).flatMap((r, i) => [
      { path: `economy.facilities.refinery.${i}.purchase_or_upgrade_black_coins`, label: `LV${r.level} · preço`, value: r.purchase_or_upgrade_black_coins },
      { path: `economy.facilities.refinery.${i}.extraction_cost_black_coins`, label: `LV${r.level} · custo extração`, value: r.extraction_cost_black_coins },
      { path: `economy.facilities.refinery.${i}.drop_min`, label: `LV${r.level} · gotas mín.`, value: r.drop_min },
      { path: `economy.facilities.refinery.${i}.drop_max`, label: `LV${r.level} · gotas máx.`, value: r.drop_max },
    ]),
  });
  g.push({
    title: 'Envasadora e barris (por nível)',
    fields: [
      ...(fac.bottling || []).map((b, i) => ({
        path: `economy.facilities.bottling.${i}.purchase_or_upgrade_black_coins`,
        label: `Envasadora LV${b.level} · preço`, value: b.purchase_or_upgrade_black_coins })),
      ...(cfg?.economy?.barrels || []).flatMap((b, i) => [
        { path: `economy.barrels.${i}.oil_drops_required`, label: `Barril LV${b.level} · gotas`, value: b.oil_drops_required },
        { path: `economy.barrels.${i}.fox_dollar_value`, label: `Barril LV${b.level} · vale FOX`, value: b.fox_dollar_value },
      ]),
    ],
  });
  g.push({
    title: 'Serviços da Máfia',
    fields: (cfg?.mafia?.services || []).flatMap((s, i) => [
      { path: `mafia.services.${i}.cost_black_coins`, label: `${s.name} · custo`, value: s.cost_black_coins },
      { path: `mafia.services.${i}.success_probability`, label: `${s.name} · chance (0–1)`, value: s.success_probability, step: 0.01 },
      { path: `mafia.services.${i}.damage_pct`, label: `${s.name} · dano %`, value: s.damage_pct, step: 0.1 },
      ...(s.facility_damage ? [{ path: `mafia.services.${i}.facility_damage.points`, label: `${s.name} · dano instalação`, value: s.facility_damage.points }] : []),
    ]),
  });
  g.push({
    title: 'Limites da Máfia',
    fields: Object.entries(cfg?.mafia?.limits || {})
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => ({ path: `mafia.limits.${k}`, label: k, value: v, step: Number.isInteger(v) ? 1 : 0.1 })),
  });
  const daily = cfg?.missions?.daily || {};
  g.push({
    title: 'Missões diárias e sequência',
    fields: [
      ...Object.entries(daily).flatMap(([id, m]) => [
        { path: `missions.daily.${id}.target`, label: `${id} · alvo`, value: m.target },
        { path: `missions.daily.${id}.reward`, label: `${id} · recompensa`, value: m.reward },
      ]),
      { path: 'missions.streak.target', label: 'sequência · dias', value: cfg?.missions?.streak?.target },
      { path: 'missions.streak.reward', label: 'sequência · recompensa', value: cfg?.missions?.streak?.reward },
    ],
  });
  g.push({
    title: 'Rankings e jackpot',
    fields: [
      { path: 'mafia.wanted_list.size', label: 'Procurados · tamanho da lista', value: cfg?.mafia?.wanted_list?.size },
      ...(cfg?.mafia?.wanted_list?.prizes || []).map((p, i) => ({
        path: `mafia.wanted_list.prizes.${i}.coins`, label: `Procurados ${p.rank}º · moedas`, value: p.coins })),
      ...(cfg?.economy?.jackpot_payout_top10 || []).map((p, i) => ({
        path: `economy.jackpot_payout_top10.${i}.pct`, label: `Jackpot Magnatas ${p.rank}º · %`, value: p.pct, step: 0.1 })),
    ],
  });
  return g;
};

export default function OilAdminConfig({ seasonId, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [version, setVersion] = useState(1);
  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        api.get(`/admin/oil/season/${seasonId}/config`),
        api.get(`/admin/oil/season/${seasonId}/config-history`),
      ]);
      setCfg(c.data.config);
      setVersion(c.data.config_version);
      setJsonText(JSON.stringify(c.data.config, null, 2));
      setHistory(h.data.history || []);
      setEdits({});
    } catch { toast.error('Erro ao carregar configuração.'); }
  }, [seasonId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError('');
    const changes = Object.entries(edits).map(([path, value]) => ({ path, value }));
    if (!changes.length) { setError('Nada foi alterado.'); toast.error('Nada foi alterado.'); return; }
    if (reason.trim().length < 3) {
      setError('Informe o motivo da alteração.');
      toast.error('Informe o motivo da alteração.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/admin/oil/season/${seasonId}/config-fields`, { changes, reason });
      toast.success(`${r.data.changed} campo(s) salvos — versão ${r.data.config_version}.`);
      setReason('');
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar.'); }
    setBusy(false);
  };

  const saveJson = async () => {
    if (reason.trim().length < 3) { toast.error('Informe o motivo da alteração.'); return; }
    let parsed;
    try { parsed = JSON.parse(jsonText); } catch { toast.error('JSON inválido.'); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/oil/season/${seasonId}/config`, { config: parsed });
      toast.success('Configuração completa salva.');
      setReason('');
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar JSON.'); }
    setBusy(false);
  };

  if (!cfg) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-3" data-testid="oil-admin-config">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold" data-testid="oil-config-version">
          versão atual: {version}
        </span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="oil-config-reason"
          placeholder="Motivo da alteração (obrigatório)" className="h-9 flex-1 rounded-lg border px-2 text-xs" />
        {!jsonMode ? (
          <Button size="sm" disabled={busy} data-testid="oil-config-save" onClick={save}
            className="bg-orange-500 text-white hover:bg-orange-600">
            <Save className="mr-1 h-3.5 w-3.5" /> Salvar {Object.keys(edits).length || ''} alteração(ões)
          </Button>
        ) : (
          <Button size="sm" disabled={busy} data-testid="oil-config-save-json" onClick={saveJson}
            className="bg-slate-800 text-white hover:bg-slate-900">
            <Save className="mr-1 h-3.5 w-3.5" /> Salvar JSON completo
          </Button>
        )}
        <Button size="sm" variant="outline" data-testid="oil-config-json-toggle"
          onClick={() => setJsonMode((v) => !v)}>
          <Code2 className="mr-1 h-3.5 w-3.5" /> {jsonMode ? 'Voltar ao formulário' : 'Editar JSON'}
        </Button>
      </div>

      {error && (
        <p className="rounded-lg border border-rose-300 bg-rose-50 p-2 text-xs font-bold text-rose-700"
          data-testid="oil-config-error">{error}</p>
      )}

      {jsonMode ? (
        <textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} data-testid="oil-config-json"
          spellCheck={false} className="h-96 w-full rounded-xl border p-2 font-mono text-[11px]" />
      ) : (
        <div className="space-y-3">
          <OilAdminJackpotRate seasonId={seasonId} onSaved={() => { load(); onSaved?.(); }} />
          <OilAdminMissions seasonId={seasonId} onSaved={() => { load(); onSaved?.(); }} />
          {buildGroups(cfg).map((g) => (
            <div key={g.title} className="rounded-xl border p-3">
              <p className="mb-2 text-xs font-black uppercase text-slate-600">{g.title}</p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {g.fields.map((f) => (
                  <label key={f.path} className="text-[10px] font-bold text-slate-500">
                    <span className="block truncate" title={f.path}>{f.label}</span>
                    <input type="number" step={f.step || 1} data-testid={`oil-cfg-${f.path}`}
                      value={edits[f.path] ?? f.value ?? 0}
                      onChange={(e) => setEdits((p) => ({ ...p, [f.path]: Number(e.target.value) }))}
                      className={`mt-0.5 h-8 w-full rounded-lg border px-2 text-xs ${edits[f.path] !== undefined ? 'border-orange-400 bg-orange-50' : ''}`} />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border p-3" data-testid="oil-config-history">
        <p className="mb-1 flex items-center gap-1 text-xs font-black uppercase text-slate-600">
          <History className="h-3.5 w-3.5" /> Histórico de versões
        </p>
        {history.length === 0 && <p className="text-[11px] text-slate-400">Nenhuma alteração registrada ainda.</p>}
        {history.map((h, i) => (
          <div key={i} className="border-b py-1 text-[11px]">
            <p className="font-bold">v{h.config_version} → v{h.config_version + 1} · {fmtDate(h.archived_at)}
              {h.reason && <span className="ml-1 font-normal text-slate-500">· {h.reason}</span>}</p>
            {(h.changes || []).map((c, j) => (
              <p key={j} className="pl-3 text-slate-500">
                {c.path}: <b className="text-rose-600">{String(c.from)}</b> → <b className="text-emerald-600">{String(c.to)}</b>
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
